// Which of the range's flagged sessions (lib/fleetFindings.js reviewPlaces) a
// search reaches: the session match (getSessionMatches) asked of them by id,
// the search's review clauses aside (those are read in the browser). Read
// once per range and search and shared by everything that needs it: the
// welcome board's counts at each place review left sessions, the board and
// the lists drilled down to a place, and the searches kept on workspaces. A
// click on a place asks nothing more.
//
// Kept frugal, as the page asks it on every search:
// - The ids are asked SESSION_MATCH_CAP at a time (under the planner's list
//   limit), and only those not asked yet: the range's findings growing asks
//   only the new ones.
// - One ask is in flight at a time, across the page; the rest wait their
//   turn. A search that moves on drops the asks it queued and has not sent.
// - What was read is kept KEEP_MS, for the few most recent searches.
import { useEffect, useMemo, useRef, useState } from "react";
import { getAuthContextEpoch } from "./authTokenStore.js";
import { query } from "./dataClient.js";
import { SESSION_MATCH_CAP } from "./sessionMatch.js";

const KEEP_MS = 120_000;
const MAX_KEPT = 12;

// By search: { at, asked: Set(id), reached: Map(id -> session), meta, pending: Map(id -> chunk) }.
const entries = new Map();
// The asks waiting their turn, oldest first, and the one in flight.
const lane = [];
let running = null;

const lower = (id) => String(id || "").toLowerCase();
const rangeKey = (range) => (typeof range === "string" ? range : JSON.stringify(range ?? null));
const restKey = (rest) => String(rest || "").replace(/\s+/g, " ").trim();
const keyOf = (range, rest) => JSON.stringify([getAuthContextEpoch(), rangeKey(range), restKey(rest)]);

function entryFor(key) {
  const cur = entries.get(key);
  if (cur && Date.now() - cur.at < KEEP_MS) return cur;
  const next = { at: Date.now(), asked: new Set(), reached: new Map(), meta: null, pending: new Map() };
  entries.delete(key);
  entries.set(key, next);
  while (entries.size > MAX_KEPT) entries.delete(entries.keys().next().value);
  return next;
}

function pump() {
  if (running) return;
  while (lane.length && !lane[0].waiters.size) drop(lane.shift());
  const chunk = lane.shift();
  if (!chunk) return;
  running = chunk;
  query("getSessionMatches", { scopePath: [{ dim: "sessions", ids: chunk.ids, id: chunk.ids[0] }], range: chunk.range, query: chunk.rest })
    .then((out) => {
      const { entry } = chunk;
      for (const s of out?.sessions || []) entry.reached.set(lower(s.id), s);
      for (const id of chunk.ids) {
        entry.asked.add(id);
        entry.pending.delete(id);
      }
      if (!entry.meta) entry.meta = { clauses: out?.clauses || [], prefiltered: !!out?.prefiltered, warnings: out?.warnings || [], searchedConversations: !!out?.searchedConversations };
      for (const w of chunk.waiters) w.landed(chunk);
    }, (error) => {
      for (const id of chunk.ids) chunk.entry.pending.delete(id);
      for (const w of chunk.waiters) w.failed(error);
    })
    .finally(() => {
      if (running === chunk) running = null;
      pump();
    });
}

// A queued ask no one waits for any more: its ids are asked again by
// whoever needs them next.
function drop(chunk) {
  for (const id of chunk.ids) if (chunk.entry.pending.get(id) === chunk) chunk.entry.pending.delete(id);
}

const answerOf = (entry) => ({ reached: entry.reached, meta: entry.meta || { clauses: [], prefiltered: false, warnings: [], searchedConversations: false } });

/**
 * What a search reaches of `ids` (flagged sessions), already read: the
 * answer when every one of them has been asked, else null.
 */
export function heldFlaggedReach({ range, rest = "", ids }) {
  const entry = entries.get(keyOf(range, rest));
  if (!entry || Date.now() - entry.at >= KEEP_MS) return null;
  return (ids || []).every((id) => entry.asked.has(lower(id))) ? answerOf(entry) : null;
}

/**
 * Asks which of `ids` a search (`rest`, the search without its review
 * clauses) reaches in `range`. -> { promise, cancel }: the promise resolves
 * to { reached: Map(id -> the match's session), meta }, the map holding at
 * least what it reaches of `ids`; `cancel` lets go of asks not sent yet.
 */
export function askFlaggedReach({ range, rest = "", ids }) {
  const entry = entryFor(keyOf(range, rest));
  const want = [...new Set((ids || []).map(lower).filter(Boolean))].sort();
  const waits = new Set();
  const fresh = [];
  for (const id of want) {
    if (entry.asked.has(id)) continue;
    const chunk = entry.pending.get(id);
    if (chunk) waits.add(chunk);
    else fresh.push(id);
  }
  for (let i = 0; i < fresh.length; i += SESSION_MATCH_CAP) {
    const chunk = { entry, ids: fresh.slice(i, i + SESSION_MATCH_CAP), range, rest: restKey(rest), waiters: new Set() };
    for (const id of chunk.ids) entry.pending.set(id, chunk);
    lane.push(chunk);
    waits.add(chunk);
  }
  let waiter = null;
  const promise = waits.size
    ? new Promise((resolve, reject) => {
      const left = new Set(waits);
      waiter = {
        landed: (chunk) => {
          left.delete(chunk);
          chunk.waiters.delete(waiter);
          if (!left.size) resolve(answerOf(entry));
        },
        failed: (error) => {
          for (const c of left) c.waiters.delete(waiter);
          left.clear();
          reject(error);
        },
      };
      for (const c of waits) c.waiters.add(waiter);
      pump();
    })
    : Promise.resolve(answerOf(entry));
  return {
    promise,
    // Every one of them asked already: nothing to wait for.
    held: !waits.size,
    cancel: () => {
      if (!waiter) return;
      for (const c of waits) c.waiters.delete(waiter);
    },
  };
}

const IDLE = Object.freeze({ status: "idle", reached: null, meta: null, error: null });
const LOADING = Object.freeze({ status: "loading", reached: null, meta: null, error: null });

/**
 * What a search reaches of `ids`, as a hook: { status, reached, meta, error }.
 * Null `ids` asks nothing. An answer already read is given at once.
 */
export function useFlaggedReach(range, rest, ids, { enabled = true } = {}) {
  const on = enabled && !!ids;
  const rk = rangeKey(range);
  // Read already (a click on a place the page counted): the same answer for
  // as long as what is asked holds still, so what is drawn from it is not
  // drawn again.
  const held = useMemo(() => {
    const answer = on ? heldFlaggedReach({ range, rest, ids }) : null;
    return answer ? { status: "done", ...answer, error: null } : null;
  }, [on, ids, rest, rk]); // eslint-disable-line react-hooks/exhaustive-deps -- the range is asked by its key
  const heldRef = useRef(held);
  heldRef.current = held;
  const [got, setGot] = useState(null);
  useEffect(() => {
    if (!on) return undefined;
    const ask = askFlaggedReach({ range, rest, ids });
    if (ask.held && heldRef.current) return undefined;
    let live = true;
    ask.promise.then(
      (answer) => { if (live) setGot({ ids, rest, rk, status: "done", ...answer, error: null }); },
      (error) => { if (live) setGot({ ids, rest, rk, status: "error", reached: null, meta: null, error }); },
    );
    return () => {
      live = false;
      ask.cancel();
    };
  }, [on, ids, rest, rk]); // eslint-disable-line react-hooks/exhaustive-deps -- the range is asked by its key
  if (!on) return IDLE;
  if (got && got.ids === ids && got.rest === rest && got.rk === rk) return got;
  return held || LOADING;
}

// Tests start from nothing read.
export function clearFlaggedReach() {
  entries.clear();
  lane.length = 0;
  running = null;
}
